import React, { useState, useEffect } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  Calendar,
  RefreshCw,
  Minus,
  DatabaseZap
} from 'lucide-react';
import { runEndpoint } from '@/services/flow';
import { toast } from 'sonner';

export default function FieldChangeHistory({ dataSourceId, fieldName, recordKey, masterFieldEntry, highlightDatePath, currentDatePath, onDateClick }) {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dataSourceId && fieldName) {
      loadHistory();
    }
  }, [dataSourceId, fieldName, recordKey]);

  const loadHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await runEndpoint('cortex.get_field_change_history', {
        dataSourceId: parseInt(dataSourceId),
        fieldName,
        recordKey
      });

      if (response?.error) {
        setError(response.error);
        setHistory([]);
      } else {
        setHistory(response.history || []);
      }
    } catch (err) {
      console.error('Error loading history:', err);
      setError('Failed to load change history');
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDatePath = (datePath) => {
    if (!datePath) return 'Unknown';
    const match = datePath.match(/year=(\d{4})\/month=(\d{2})\/day=(\d{2})/);
    if (!match) return datePath;
    return `${match[1]}-${match[2]}-${match[3]}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6 text-slate-500">
        {error}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-6 text-slate-500">
        No change history available for this field
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Timeline Header */}
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <Calendar className="h-4 w-4" />
        <span>Change history (newest first)</span>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />

        {/* Master record value */}
        {masterFieldEntry && (
          <div className="relative flex gap-4 pb-4">
            <div className="relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 bg-green-100 border-green-500">
              <DatabaseZap className="h-4 w-4 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">Master Record</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Approved
                </Badge>
                {masterFieldEntry.data_source_id && (
                  <span className="text-xs text-slate-400">ds:{masterFieldEntry.data_source_id}</span>
                )}
              </div>
              <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                <div className="text-sm text-slate-600 mb-1">Value:</div>
                <div className="font-mono text-sm break-all">
                  {masterFieldEntry.value ?? <span className="text-slate-400 italic">empty</span>}
                </div>
                {masterFieldEntry.source_date_path && (
                  <div className="text-xs text-slate-500 mt-2">
                    Source: {formatDatePath(masterFieldEntry.source_date_path)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {history.map((entry, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === history.length - 1;
          const prevEntry = idx > 0 ? history[idx - 1] : null;
          const valueChanged = entry.changed;
          const isMasterSource = highlightDatePath && entry.datePath === highlightDatePath;
          const isCurrent = currentDatePath ? entry.datePath === currentDatePath : isFirst;

          return (
            <div key={idx} className="relative flex gap-4 pb-4">
              {/* Timeline dot */}
              <div
                className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  isMasterSource
                    ? 'bg-green-100 border-green-500'
                    : isCurrent
                    ? 'bg-blue-100 border-blue-500'
                    : valueChanged
                    ? 'bg-blue-100 border-blue-500'
                    : 'bg-slate-50 border-slate-300'
                }`}
              >
                {isMasterSource ? (
                  <DatabaseZap className="h-4 w-4 text-green-600" />
                ) : valueChanged ? (
                  <RefreshCw className="h-4 w-4 text-blue-500" />
                ) : (
                  <Minus className="h-4 w-4 text-slate-400" />
                )}
              </div>

              {/* Content */}
              <div className={`flex-1 pb-2 ${isFirst ? 'pt-0' : ''}`}>
                <div className="flex items-center gap-2 mb-1">
                  {onDateClick ? (
                    <button
                      className={`font-medium hover:underline ${isCurrent ? 'text-blue-700' : 'text-blue-600 hover:text-blue-800'}`}
                      onClick={() => onDateClick(entry.datePath)}
                    >
                      {formatDatePath(entry.datePath)}
                    </button>
                  ) : (
                    <span className="font-medium">{formatDatePath(entry.datePath)}</span>
                  )}
                  {isMasterSource && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      In Master
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      Current
                    </Badge>
                  )}
                  {isLast && (
                    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                      Initial
                    </Badge>
                  )}
                  {valueChanged && !isLast && (
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                      Changed
                    </Badge>
                  )}
                </div>

                <div className={`p-3 rounded-lg border ${
                  isMasterSource
                    ? 'bg-green-50 border-green-200'
                    : valueChanged
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="text-sm text-slate-600 mb-1">Value:</div>
                  <div className="font-mono text-sm break-all">
                    {entry.value || <span className="text-slate-400 italic">empty</span>}
                  </div>

                  {entry.date_changed && (
                    <div className="text-xs text-slate-500 mt-2">
                      Last changed: {formatDatePath(entry.date_changed)}
                    </div>
                  )}
                </div>

                {/* Show change indicator between entries */}
                {prevEntry && prevEntry.value !== entry.value && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                    <ArrowRight className="h-3 w-3" />
                    <span>Changed from previous value</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="pt-4 border-t text-sm text-slate-500">
        <span className="font-medium">{history.filter(h => h.changed).length}</span> changes detected across <span className="font-medium">{history.length}</span> data snapshots
      </div>
    </div>
  );
}
